(() => {
  if (window.CurlBundleBuilder) {
    window.CurlBundleBuilder.mount(document);
    return;
  }
  const instances = new Map();
  const selector = '[data-curl-bundle-builder]';
  const text = (root, hook, value) => {
    const element = root.querySelector(hook);
    if (element && element.textContent !== String(value)) element.textContent = String(value);
  };
  const fill = (copy = '', values = {}) => String(copy).replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match);
  const cents = (formatted, currency) => {
    const parts = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).formatToParts(1234.5);
    const decimal = parts.find((part) => part.type === 'decimal')?.value || '.';
    const value = String(formatted || '').replace(/[^\d.,-]/g, '');
    const normalized = decimal === ',' ? value.replaceAll('.', '').replace(',', '.') : value.replaceAll(',', '');
    return Math.round((Number(normalized) || 0) * 100);
  };

  const initialize = (root) => {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const settings = JSON.parse(root.querySelector('[data-bundle-settings]')?.textContent || '{}');
    const cards = [...root.querySelectorAll('[data-bundle-product]')].map((element) => ({
      element,
      product: JSON.parse(element.querySelector('[data-bundle-product-json]')?.textContent || '{}')
    }));
    const dialog = root.querySelector('[data-bundle-variant-dialog]');
    const status = root.querySelector('[data-bundle-status]');
    const appSelector = settings.appWidgetSelector || root.dataset.appWidgetSelector || '#rebuy-widget-297740';
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: settings.currency || 'USD' });
    let adapter = null;
    let appRoot = null;
    let originalHidden = false;
    let originalAria = null;
    let originalDisplay = '';
    let originalDisplayPriority = '';
    let unsubscribe = null;
    let operation = false;
    let submitting = false;
    let submittedCartCount = 0;
    let added = false;
    let addedSelectionKey = '';
    let submissionTimer = null;
    let selectedCard = null;
    let returnFocus = null;
    let lastState = null;
    let discoveryTimer = null;
    let discoveryObserver = null;

    const showStatus = (message = '', error = false) => {
      if (!status) return;
      status.textContent = message;
      status.hidden = !message;
      status.dataset.state = error ? 'error' : '';
    };
    const revealApp = () => {
      if (!appRoot) return;
      appRoot.hidden = originalHidden;
      if (originalDisplay) appRoot.style.setProperty('display', originalDisplay, originalDisplayPriority);
      else appRoot.style.removeProperty('display');
      if (originalAria === null) appRoot.removeAttribute('aria-hidden');
      else appRoot.setAttribute('aria-hidden', originalAria);
    };
    const closeDialog = () => {
      if (dialog?.open) dialog.close();
      if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
      returnFocus = null;
      selectedCard = null;
    };
    const variantChoices = (state) => {
      if (!selectedCard || !dialog?.open) return;
      const nativeProduct = state.products.find((product) => String(product.id) === String(selectedCard.product.id));
      dialog.querySelectorAll('[data-bundle-variant-choice]').forEach((button) => {
        const variant = nativeProduct?.variants.find((item) => String(item.id) === button.dataset.bundleVariantChoice);
        button.disabled = operation || state.busy || !variant?.available || state.count >= state.max;
      });
    };
    const render = (state) => {
      lastState = state;
      const selectionKey = JSON.stringify(state.lines.map((line) => [line.productId, line.variantId]));
      if (added && selectionKey !== addedSelectionKey) added = false;
      const busy = operation || submitting || state.busy;
      text(root, '[data-bundle-count]', state.count);
      text(root, '[data-bundle-max]', state.max || '—');
      const progress = root.querySelector('[data-bundle-progress]');
      if (progress) progress.style.width = `${Math.max(0, Math.min(100, state.progress))}%`;
      const tiers = state.tiers.filter((tier) => tier.count > 0);
      tiers.slice(0, 2).forEach((tier, index) => {
        const element = root.querySelector(index ? '[data-bundle-tier-two]' : '[data-bundle-tier-one]');
        if (element) {
          element.textContent = `${tier.count}${index ? '+' : ''} · ${tier.value}`;
          element.classList.toggle('is-unlocked', state.count >= tier.count);
        }
        text(root, `[data-bundle-hero-number="${index}"]`, `${tier.count}${index ? '+' : ''}`);
        text(root, `[data-bundle-hero-saving="${index}"]`, fill(settings.saveLabel || 'Save {discount}', { discount: tier.value }));
      });
      const nextTier = tiers.find((tier) => state.count < tier.count);
      text(root, '[data-bundle-next]', nextTier
        ? fill(settings.nextTierLabel || '{count} MORE TO SAVE {discount}', { count: nextTier.count - state.count, discount: nextTier.value })
        : settings.bestSavingsLabel || '');
      text(root, '[data-bundle-subtotal]', state.subtotalText || money.format(0));
      text(root, '[data-bundle-total]', state.totalText || money.format(0));
      const savings = Math.max(0, cents(state.subtotalText, settings.currency || 'USD') - cents(state.totalText, settings.currency || 'USD'));
      text(root, '[data-bundle-discount]', `−${money.format(savings / 100)}`);
      const eligibleTier = [...tiers].reverse().find((tier) => state.count >= tier.count);
      text(root, '[data-bundle-discount-label]', eligibleTier ? ` · ${eligibleTier.value}` : '');
      cards.forEach(({ element, product }) => {
        const native = state.products.find((item) => String(item.id) === String(product.id));
        const count = state.lines.filter((line) => String(line.productId) === String(product.id)).length;
        const counter = element.querySelector('[data-bundle-product-count]');
        if (counter) {
          counter.hidden = !count;
          counter.textContent = fill(settings.inBundleLabel, { count });
        }
        const add = element.querySelector('[data-bundle-add], [data-bundle-variant-open]');
        if (add) {
          const full = state.max > 0 && state.count >= state.max;
          const available = native && native.variants.some((variant) => variant.available);
          add.disabled = busy || !available || full || (!add.hasAttribute('data-bundle-variant-open') && !native.canAdd);
          add.textContent = !available ? settings.unavailableLabel : full ? settings.fullLabel : add.dataset.addLabel || settings.addLabel;
        }
      });
      const selections = root.querySelector('[data-bundle-selections]');
      const fragment = document.createDocumentFragment();
      if (!state.lines.length) fragment.append(root.querySelector('[data-bundle-empty-template]').content.cloneNode(true));
      state.lines.forEach((line) => {
        const card = cards.find(({ product }) => String(product.id) === String(line.productId));
        const item = root.querySelector('[data-bundle-line-template]').content.cloneNode(true);
        const title = card?.product.title || line.title;
        const description = line.variantTitle && line.variantTitle !== 'Default Title' ? `${title} · ${line.variantTitle}` : title;
        const imageMarker = item.querySelector('[data-bundle-line-image]');
        const imageTemplate = card?.element.querySelector(`template[data-bundle-variant-image="${line.variantId}"]`) || card?.element.querySelector('template[data-bundle-product-image]');
        const image = imageTemplate?.content.querySelector('img')?.cloneNode(true) || document.createElement('img');
        const variant = card?.product.variants.find((candidate) => String(candidate.id) === String(line.variantId));
        const source = variant?.image || card?.product.image || line.image;
        if (!image.getAttribute('src') && source) image.src = source;
        image.hidden = !image.getAttribute('src');
        image.alt = card?.product.imageAlt || '';
        imageMarker.replaceWith(image);
        item.querySelector('[data-bundle-line-title]').textContent = description;
        item.querySelector('[data-bundle-line-price]').textContent = line.priceText;
        const remove = item.querySelector('[data-bundle-remove]');
        remove.dataset.bundleRemove = String(line.index);
        remove.disabled = busy;
        remove.setAttribute('aria-label', fill(settings.removeLabel, { product: description }));
        fragment.append(item);
      });
      selections.replaceChildren(fragment);
      const clear = root.querySelector('[data-bundle-clear]');
      clear.disabled = busy || !state.count;
      const submit = root.querySelector('[data-bundle-submit]');
      submit.disabled = busy || (!added && !state.canSubmit);
      submit.textContent = busy ? settings.workingLabel : added ? settings.viewBagLabel : state.canSubmit ? settings.submitLabel : fill(settings.minimumLabel, { minimum: tiers[0]?.count || '—' });
      variantChoices(state);
      if (state.errors?.length) {
        showStatus(state.errors.join(' '), true);
        revealApp();
      }
    };
    const perform = async (action) => {
      if (!adapter || operation || submitting) return;
      operation = true;
      showStatus();
      render(adapter.read());
      try {
        await action();
      } catch (error) {
        showStatus(settings.errorMessage || root.dataset.errorMessage, true);
        revealApp();
      } finally {
        operation = false;
        if (adapter) render(adapter.read());
      }
    };
    const openVariants = (card, trigger) => {
      const native = lastState.products.find((product) => String(product.id) === String(card.product.id));
      if (!native) return;
      selectedCard = card;
      returnFocus = trigger;
      text(root, '[data-bundle-variant-heading]', card.product.title);
      const options = root.querySelector('[data-bundle-variant-options]');
      options.replaceChildren();
      native.variants.forEach((variant) => {
        const button = document.createElement('button');
        const name = document.createElement('span');
        const label = document.createElement('span');
        button.type = 'button';
        button.dataset.bundleVariantChoice = String(variant.id);
        name.textContent = variant.title;
        label.textContent = variant.available ? settings.selectLabel : settings.unavailableLabel;
        button.append(name, label);
        options.append(button);
      });
      dialog.showModal();
      variantChoices(lastState);
    };
    root.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const add = target.closest('[data-bundle-add]');
      const variantOpen = target.closest('[data-bundle-variant-open]');
      const choice = target.closest('[data-bundle-variant-choice]');
      const remove = target.closest('[data-bundle-remove]');
      if (target.closest('[data-bundle-variant-close]') || target === dialog) {
        closeDialog();
      } else if (add) {
        event.preventDefault();
        const card = add.closest('[data-bundle-product]');
        perform(() => adapter.add(card.dataset.productId, add.dataset.variantId));
      } else if (variantOpen && adapter && !operation) {
        event.preventDefault();
        openVariants(cards.find((card) => card.element === variantOpen.closest('[data-bundle-product]')), variantOpen);
      } else if (choice && selectedCard) {
        event.preventDefault();
        const productId = selectedCard.product.id;
        const variantId = choice.dataset.bundleVariantChoice;
        perform(async () => { await adapter.add(productId, variantId); closeDialog(); });
      } else if (remove) {
        event.preventDefault();
        perform(() => adapter.remove(Number(remove.dataset.bundleRemove)));
      } else if (target.closest('[data-bundle-clear]')) {
        event.preventDefault();
        perform(() => adapter.clear());
      } else if (target.closest('[data-bundle-submit]') && added) {
        window.location.assign(settings.cartUrl);
      } else if (target.closest('[data-bundle-submit]') && adapter && !submitting && !operation && adapter.read().canSubmit) {
        event.preventDefault();
        submitting = true;
        submittedCartCount = Number(document.querySelector('[data-curl-cart-drawer]')?.dataset.cartItemCount || 0);
        showStatus();
        render(adapter.read());
        adapter.submit().catch(() => { showStatus(settings.errorMessage, true); revealApp(); }).finally(() => {
          submissionTimer = window.setTimeout(() => { submitting = false; if (adapter) render(adapter.read()); }, 15000);
        });
      }
    }, { signal: controller.signal });
    dialog?.addEventListener('cancel', () => { returnFocus?.focus({ preventScroll: true }); selectedCard = null; }, { signal: controller.signal });
    root.querySelectorAll('[data-bundle-rating]').forEach((rating) => {
      const value = Number(rating.dataset.bundleRating) || 0;
      rating.querySelectorAll('.collection-star').forEach((star, index) => star.style.setProperty('--star-fill', `${Math.max(0, Math.min(100, (value - index) * 100))}%`));
    });
    const connect = () => {
      if (controller.signal.aborted || !window.CurlRebuyBundleAdapter) return;
      if (adapter && appRoot?.isConnected) return;
      if (adapter) {
        unsubscribe?.();
        adapter.destroy();
        revealApp();
        adapter = null;
        appRoot = null;
        submitting = false;
        window.clearTimeout(submissionTimer);
        root.querySelectorAll('[data-bundle-add], [data-bundle-variant-open], [data-bundle-submit], [data-bundle-clear]').forEach((button) => { button.disabled = true; });
        showStatus(settings.waitingMessage);
      }
      let candidate;
      try { candidate = document.querySelector(appSelector); } catch { showStatus(settings.errorMessage, true); return; }
      if (!candidate) return;
      candidate = candidate.matches('.rebuy-bundle-builder') ? candidate : candidate.querySelector('.rebuy-bundle-builder');
      if (!candidate || root.contains(candidate)) return;
      const connection = new window.CurlRebuyBundleAdapter(candidate);
      const state = connection.read();
      if (!state.max || !state.products.length) { connection.destroy(); return; }
      appRoot = candidate;
      adapter = connection;
      originalHidden = appRoot.hidden;
      originalAria = appRoot.getAttribute('aria-hidden');
      originalDisplay = appRoot.style.getPropertyValue('display');
      originalDisplayPriority = appRoot.style.getPropertyPriority('display');
      if (settings.hideAppWidget !== false) {
        appRoot.hidden = true;
        appRoot.style.setProperty('display', 'none', 'important');
        appRoot.setAttribute('aria-hidden', 'true');
      }
      window.clearTimeout(discoveryTimer);
      unsubscribe = adapter.subscribe(render);
      showStatus();
      render(state);
    };
    discoveryObserver = new MutationObserver(connect);
    discoveryObserver.observe(document.body, { childList: true, subtree: true });
    discoveryTimer = window.setTimeout(() => { if (!adapter) showStatus(settings.errorMessage, true); }, 20000);
    for (const name of ['rebuy.ready', 'rebuy.addProductToBundle', 'rebuy.removeProductFromBundle', 'rebuy.addBundleToCart']) {
      document.addEventListener(name, () => { connect(); if (adapter) render(adapter.read()); }, { signal: controller.signal });
    }
    document.addEventListener('curl:cart-drawer:updated', (event) => {
      if (!submitting || !adapter) return;
      if (event.detail?.open && Number(event.detail.itemCount) > submittedCartCount) {
        added = true;
        addedSelectionKey = JSON.stringify(adapter.read().lines.map((line) => [line.productId, line.variantId]));
      }
      submitting = false;
      window.clearTimeout(submissionTimer);
      if (adapter) render(adapter.read());
    }, { signal: controller.signal });
    document.addEventListener('curl:cart-drawer:error', () => {
      if (!submitting) return;
      submitting = false;
      window.clearTimeout(submissionTimer);
      showStatus(settings.errorMessage, true);
      revealApp();
      if (adapter) render(adapter.read());
    }, { signal: controller.signal });
    instances.set(root, {
      destroy() {
        closeDialog();
        controller.abort();
        discoveryObserver.disconnect();
        window.clearTimeout(discoveryTimer);
        window.clearTimeout(submissionTimer);
        unsubscribe?.();
        adapter?.destroy();
        revealApp();
        adapter = null;
        instances.delete(root);
      }
    });
    connect();
  };
  const mount = (scope = document) => {
    if (scope.matches?.(selector)) initialize(scope);
    scope.querySelectorAll(selector).forEach(initialize);
  };
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, root) => { if (event.target === root || event.target.contains(root)) instance.destroy(); });
  });
  window.CurlBundleBuilder = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
})();
